// Рассылка протокола (033: contracts/api.md, «Рассылка протокола»; data-model.md,
// `delivery.protocol_mailings`; FR-010 – FR-013; ASG-RES-04). Состояния «выключена / в тени /
// включена»: после перевода и создания — «выключена»; «включена» — только из «в тени» (Д11).
// Окно последствий считает людей правилом «Первого письма» 030; 031 заменит его окном готовности.
import { z } from '../kernel/zod.ts';
import { PersonRefSchema, TimestampSchema } from '../kernel/common.ts';
import { FirstLetterSkipReasonSchema } from './first-letter.ts';

export const mailingStates = ['off', 'shadow', 'on'] as const;
export type MailingState = (typeof mailingStates)[number];
export const MailingStateSchema = z.enum(mailingStates);

export const mailingStateLabels = {
  off: 'выключена',
  shadow: 'в тени',
  on: 'включена',
} as const satisfies Record<MailingState, string>;

/** Разрешённые переходы (FR-010): «включена» — только из «в тени»; «выключить» — из любого. */
export const mailingTransitions = {
  off: ['shadow'],
  shadow: ['on', 'off'],
  on: ['off'],
} as const satisfies Record<MailingState, readonly MailingState[]>;

export function mailingTransitionAllowed(from: MailingState, to: MailingState): boolean {
  return (mailingTransitions[from] as readonly MailingState[]).includes(to);
}

/** Кто получит письма по протоколу и кто нет — числами, без ФИО (FR-012). */
export const MailingAudienceSchema = z.object({
  ready: z.number().int().min(0),
  skipped: z.array(
    z.object({ reason: FirstLetterSkipReasonSchema, count: z.number().int().min(1) }),
  ),
  /** В песочнице — готовых с адресом из списка; боевой режим — null. */
  sandboxReady: z.number().int().min(0).nullable(),
});
export type MailingAudience = z.infer<typeof MailingAudienceSchema>;

/** GET/POST /api/protocols/{id}/mailing. */
export const ProtocolMailingSchema = z.object({
  state: MailingStateSchema,
  /** null — состояние ещё не меняли («выключена»). */
  changed: z.object({ at: TimestampSchema, by: PersonRefSchema }).nullable(),
  /** У пользователя есть minutes.mailing_enable. */
  canEnable: z.boolean(),
  /** Протокол в режиме файла — null (числа — для протокола в системе). */
  audience: MailingAudienceSchema.nullable(),
});
export type ProtocolMailing = z.infer<typeof ProtocolMailingSchema>;

/** POST /api/protocols/{id}/mailing: «Включить в тени» и «Выключить рассылку». */
export const MailingSetBodySchema = z.strictObject({
  state: z.enum(['shadow', 'off']),
  expected: MailingStateSchema,
});
export type MailingSetBody = z.infer<typeof MailingSetBodySchema>;

/** POST /api/protocols/{id}/mailing/enable: «Включить рассылку» — только из «в тени». */
export const MailingEnableBodySchema = z.strictObject({
  expected: z.literal('shadow'),
});
export type MailingEnableBody = z.infer<typeof MailingEnableBodySchema>;
